import { useSyncExternalStore } from 'react';

/**
 * Readouts that change with the pointer.
 *
 * These are kept out of the main store and updated at most once per animation
 * frame, so a 60 Hz pointermove cannot re-render the shell (design B3).
 */
export interface VolatileReadout {
  readonly coordinate: string;
  readonly measurement: string;
  readonly snapRelation: string | null;
  readonly visibleBricks: number;
  readonly zoom: string;
}

const EMPTY: VolatileReadout = {
  coordinate: 'X —   Y —',
  measurement: '',
  snapRelation: null,
  visibleBricks: 0,
  zoom: '100%',
};

let current: VolatileReadout = EMPTY;
let pending: Partial<VolatileReadout> | null = null;
let frame: number | null = null;
const listeners = new Set<() => void>();

function commit(): void {
  frame = null;
  if (pending === null) return;
  const next = { ...current, ...pending };
  pending = null;
  if (
    next.coordinate === current.coordinate &&
    next.measurement === current.measurement &&
    next.snapRelation === current.snapRelation &&
    next.visibleBricks === current.visibleBricks &&
    next.zoom === current.zoom
  ) {
    return;
  }
  current = next;
  for (const listener of listeners) listener();
}

/** Coalesce updates into one commit per frame. */
export function setVolatile(patch: Partial<VolatileReadout>): void {
  pending = pending === null ? patch : { ...pending, ...patch };
  if (frame === null) {
    frame =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(commit)
        : (setTimeout(commit, 0) as unknown as number);
  }
}

export function readVolatile(): VolatileReadout {
  return current;
}

export function resetVolatile(): void {
  current = EMPTY;
  pending = null;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useVolatile(): VolatileReadout {
  return useSyncExternalStore(subscribe, readVolatile, readVolatile);
}
